import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CarouselArrow,
  CarouselWrapper,
  WalletCardHeader,
  WalletCardLabel,
  WalletSummaryCard,
  WalletSummaryCardIcon,
  WalletSummarySection,
} from "./style";
import CurrencyFlag from "../components/CurrencyFlag";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as ChevronIcon } from "../../../assets/icons/arrow-right.svg";
import { formatWalletBalance, getWalletName } from "../utils";
import { WalletSummary } from "../../../types/walletTypes";

type WalletCarouselProps = {
  wallets: WalletSummary[];
  showCreateCard: boolean;
  onWalletClick: (wallet: WalletSummary) => void;
  onCreateWallet: () => void;
};

const SCROLL_RATIO = 0.8;

const WalletCarousel = ({
  wallets,
  showCreateCard,
  onWalletClick,
  onCreateWallet,
}: WalletCarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollLeft(track.scrollLeft > 0);
    setCanScrollRight(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState, wallets, showCreateCard]);

  const scroll = (direction: 1 | -1) => {
    const track = trackRef.current;
    track?.scrollBy?.({ left: direction * track.clientWidth * SCROLL_RATIO });
  };

  return (
    <CarouselWrapper>
      {canScrollLeft && (
        <CarouselArrow
          type="button"
          $direction="left"
          aria-label="Scroll wallets left"
          data-carousel-arrow
          onClick={() => scroll(-1)}
        >
          <ChevronIcon />
        </CarouselArrow>
      )}

      <WalletSummarySection ref={trackRef} onScroll={updateScrollState}>
        {wallets.map((wallet) => (
          <WalletSummaryCard
            key={wallet.currency}
            onClick={() => onWalletClick(wallet)}
            currency={wallet.currency}
          >
            <WalletCardHeader>
              <CurrencyFlag currency={wallet.currency} length={32} />
              <WalletCardLabel>
                <strong>{wallet.currency}</strong>
                <span>{getWalletName(wallet.currency)}</span>
              </WalletCardLabel>
            </WalletCardHeader>
            <h4 className="amount" title={formatWalletBalance(wallet.currency, wallet.availableBalance)}>
              {formatWalletBalance(wallet.currency, wallet.availableBalance)}
            </h4>
          </WalletSummaryCard>
        ))}

        {showCreateCard && (
          <WalletSummaryCard onClick={onCreateWallet}>
            <WalletSummaryCardIcon>
              <PlusIcon />
            </WalletSummaryCardIcon>
            <h4>Create new wallet</h4>
          </WalletSummaryCard>
        )}
      </WalletSummarySection>

      {canScrollRight && (
        <CarouselArrow
          type="button"
          $direction="right"
          aria-label="Scroll wallets right"
          data-carousel-arrow
          onClick={() => scroll(1)}
        >
          <ChevronIcon />
        </CarouselArrow>
      )}
    </CarouselWrapper>
  );
};

export default WalletCarousel;
